import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { Tab, Tabs, Typography } from '@material-ui/core';
import { createElement, FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import _ViewportObserver from 'viewport-observer';

import Language from '../../../entity/Language';
import _OnboardingBanner from '../../component/compound/OnboardingBanner';
import _Spinner from '../../component/compound/Spinner';
import _Breadcrumbs from '../../connected/Breadcrumbs';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import AssignmentList from './AssignmentList';
import AssignmentListItem from './AssignmentList/AssignmentListItem';
import useMyAssignmentsQuery from './useMyAssignmentsQuery';

interface Props {
  language: Language;
  className?: string;
}

type TabState = 'active' | 'waiting' | 'completed' | 'canceled';

const MyAssignmentsPage: FC<Props> = ({ className, language }) => {
  const {
    count: activeCount,
    items: activeItems,
    loading: loadingActive,
    hasNext: hasNextActive,
    loadMore: loadMoreActive,
  } = useMyAssignmentsQuery('active');
  const {
    count: waitingCount,
    items: waitingItems,
    loading: loadingWaiting,
    hasNext: hasNextWaiting,
    loadMore: loadMoreWaiting,
  } = useMyAssignmentsQuery('waiting');
  const {
    count: canceledCount,
    items: canceledItems,
    loading: loadingCanceled,
    hasNext: hasNextCanceled,
    loadMore: loadMoreCanceled,
  } = useMyAssignmentsQuery('canceled');
  const {
    count: completedCount,
    items: completedItems,
    loading: loadingCompleted,
    hasNext: hasNextCompleted,
    loadMore: loadMoreCompleted,
  } = useMyAssignmentsQuery('completed');

  const [tab, setTab] = useState<TabState>('active');
  const selectableTabs: TabState[] = [];
  if (activeCount > 0) selectableTabs.push('active');
  if (waitingCount > 0) selectableTabs.push('waiting');
  if (completedCount > 0) selectableTabs.push('completed');
  if (canceledCount > 0) selectableTabs.push('canceled');

  if (tab !== 'active' && selectableTabs.length === 0) {
    // 選択可能なタブがない場合、進行中タブを初期値にする
    setTab('active');
  } else if (selectableTabs.length > 0 && !selectableTabs.includes(tab) && selectableTabs[0] !== tab) {
    // 選択可能なタブがある場合、最も左側(=配列の先頭)のタブを初期値にする
    setTab(selectableTabs[0]);
  }

  const onChangeTab = useCallback(
    (_: any, value: string) => {
      setTab(value as TabState);
    },
    [setTab],
  );

  // １ページ目のデータload
  useEffect(() => {
    if (tab == 'active' && !loadingActive && activeItems == null) {
      loadMoreActive();
    }
    if (tab == 'waiting' && !loadingWaiting && waitingItems == null) {
      loadMoreWaiting();
    }
    if (tab == 'canceled' && !loadingCanceled && canceledItems == null) {
      loadMoreCanceled();
    }
    if (tab == 'completed' && !loadingCompleted && completedItems == null) {
      loadMoreCompleted();
    }
  }, [
    tab,
    loadMoreActive,
    loadMoreCanceled,
    loadMoreWaiting,
    loadMoreCompleted,
    activeItems,
    waitingItems,
    canceledItems,
    completedItems,
    loadingActive,
    loadingWaiting,
    loadingCanceled,
    loadingCompleted,
  ]);

  return (
    <Root className={className}>
      <Breadcrumbs />

      {language === 'ja-JP' && <OnboardingBanner />}
      <StyledTabs value={tab} onChange={onChangeTab}>
        <Tab
          label={
            <TabLabel>
              <FormattedMessage id="my_assignments.tabs.active" />
              <ItemAmount>({activeCount})</ItemAmount>
            </TabLabel>
          }
          value="active"
          disabled={activeCount <= 0}
        />
        <Tab
          label={
            <TabLabel>
              <FormattedMessage id="my_assignments.tabs.waiting" />
              <ItemAmount>({waitingCount})</ItemAmount>
            </TabLabel>
          }
          value="waiting"
          disabled={waitingCount <= 0}
        />
        <Tab
          label={
            <TabLabel>
              <FormattedMessage id="my_assignments.tabs.submitted" />
              <ItemAmount>({completedCount})</ItemAmount>
            </TabLabel>
          }
          value="completed"
          disabled={completedCount <= 0}
        />
        <Tab
          label={
            <TabLabel>
              <FormattedMessage id="my_assignments.tabs.canceled" />
              <ItemAmount>({canceledCount})</ItemAmount>
            </TabLabel>
          }
          value="canceled"
          disabled={canceledCount <= 0}
        />
      </StyledTabs>

      {tab === 'active' && (
        <ViewItem>
          <AssignmentList loading={loadingActive}>
            {(activeItems || []).map((item) => (
              <AssignmentListItem assignment={item} key={`assignment_list_item-${item.id}`} />
            ))}
          </AssignmentList>
          {!loadingActive && hasNextActive && (
            <ViewportObserver
              onChange={(e) => {
                if (e.intersectionRect.top != 0) {
                  loadMoreActive();
                }
              }}
            >
              <ViewPortObserverFiller />
            </ViewportObserver>
          )}
          {loadingActive && hasNextActive && <Spinner />}
        </ViewItem>
      )}
      {tab === 'waiting' && (
        <ViewItem>
          <AssignmentList loading={loadingWaiting}>
            {(waitingItems || []).map((item) => (
              <AssignmentListItem assignment={item} key={`assignment_list_item-${item.id}`} />
            ))}
          </AssignmentList>
          {!loadingWaiting && hasNextWaiting && (
            <ViewportObserver
              onChange={(e) => {
                if (e.intersectionRect.top != 0) {
                  loadMoreWaiting();
                }
              }}
            >
              <ViewPortObserverFiller />
            </ViewportObserver>
          )}
          {loadingWaiting && hasNextWaiting && <Spinner />}
        </ViewItem>
      )}
      {tab === 'completed' && (
        <ViewItem>
          <AssignmentList loading={loadingCompleted}>
            {(completedItems || []).map((item) => (
              <AssignmentListItem assignment={item} key={`assignment_list_item-${item.id}`} />
            ))}
          </AssignmentList>
          {!loadingCompleted && hasNextCompleted && (
            <ViewportObserver
              onChange={(e) => {
                if (e.intersectionRect.top != 0) {
                  loadMoreCompleted();
                }
              }}
            >
              <ViewPortObserverFiller />
            </ViewportObserver>
          )}
          {loadingCompleted && hasNextCompleted && <Spinner />}
        </ViewItem>
      )}
      {tab === 'canceled' && (
        <ViewItem>
          <AssignmentList loading={loadingCanceled}>
            {(canceledItems || []).map((item) => (
              <AssignmentListItem assignment={item} key={`assignment_list_item-${item.id}`} />
            ))}
          </AssignmentList>
          {!loadingCanceled && hasNextCanceled && (
            <ViewportObserver
              onChange={(e) => {
                if (e.intersectionRect.top != 0) {
                  loadMoreCanceled();
                }
              }}
            >
              <ViewPortObserverFiller />
            </ViewportObserver>
          )}
          {loadingCanceled && hasNextCanceled && <Spinner />}
        </ViewItem>
      )}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['5fr', '1fr'],
    rows: ['auto', 'auto', 'auto', 'auto', 'auto', '1fr'],
    areas: [
      ['breadcrumbs', 'breadcrumbs'],
      ['onboarding', 'onboarding'],
      ['tabs', 'button'],
      ['list', 'list'],
    ],
    alignItems: 'flex-end',
  }),
  {
    margin: `0 ${SPACING_UNIT}px`,
    padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
  },
);

const Breadcrumbs = styled(_Breadcrumbs)({ ...gridLayoutItem('breadcrumbs'), marginBottom: SPACING_UNIT * 5 });

const OnboardingBanner = styled(_OnboardingBanner)({
  ...gridLayoutItem('onboarding'),
  marginBottom: SPACING_UNIT * 2,
});

const StyledTabs = styled(Tabs)(gridLayoutItem('tabs'));

const TabLabel = styled('div')(
  flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const ItemAmount = styled(Typography)({ paddingLeft: SPACING_UNIT });
ItemAmount.defaultProps = { variant: 'button' };

const fadeInUp = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

const ViewItem = styled('div')(gridLayoutItem('list'), {
  padding: `${SPACING_REGULAR}px ${SPACING_UNIT / 2}px`,
  animation: `${fadeInUp} .8s both`,
});

const ViewportObserver = styled(_ViewportObserver)();

const ViewPortObserverFiller = styled('div')({
  // MS Edge has a bug around intersection-observer with 0-area element. This filler has 1px height to avoid that.
  // See https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/17319665/
  height: 1,
});

const Spinner = styled(_Spinner)();

export default MyAssignmentsPage;
